<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const {
  content,
  attachments,
  contentAttributes,
  messageType,
  conversationId,
  isPrivate,
} = useMessageContext();

const store = useStore();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

// --- Traducción automática (feature propia) ---
// Entrantes: mientras la pestaña "Traducción" está activa en esta conversación,
// el mensaje del contacto se muestra traducido al idioma del agente y el
// original queda como subtítulo. Salientes traducidos: el content ya viaja
// traducido y lo que escribió el agente queda SIEMPRE como subtítulo.
const autoTranslationActive = computed(() =>
  store.getters['autoTranslation/getIsActive'](conversationId?.value)
);
const incomingLang = computed(() =>
  store.getters['autoTranslation/getIncomingLang'](conversationId?.value)
);

const autoIncomingTranslation = computed(() => {
  if (!autoTranslationActive.value) return null;
  // Entrantes del contacto, salientes de agentes y notas privadas (IA).
  const eligible =
    messageType.value === MESSAGE_TYPES.INCOMING ||
    messageType.value === MESSAGE_TYPES.OUTGOING ||
    isPrivate?.value;
  if (!eligible) return null;
  // Los enviados con el modo Traducción ya llevan su original de subtítulo.
  if (contentAttributes.value?.autoTranslated) return null;
  const translations = contentAttributes.value?.translations || {};
  const translated = translations[incomingLang.value];
  if (!translated) return null;
  // Si ya estaba en el idioma del agente, no hay nada que mostrar.
  if (translated.trim() === (content.value || '').trim()) return null;
  return translated;
});

const originalSubtitle = computed(() => {
  if (autoIncomingTranslation.value) return content.value;
  if (
    messageType.value === MESSAGE_TYPES.OUTGOING &&
    !isPrivate?.value &&
    contentAttributes.value?.autoTranslated &&
    contentAttributes.value?.originalContent
  ) {
    return contentAttributes.value.originalContent;
  }
  return null;
});

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (autoIncomingTranslation.value) {
    return autoIncomingTranslation.value;
  }

  if (hasTranslations.value && autoTranslationActive.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const showTranslationToggle = computed(() => {
  return (
    hasTranslations.value &&
    autoTranslationActive.value &&
    !autoIncomingTranslation.value
  );
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <BaseBubble class="px-4 py-3" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <FormattedContent v-if="renderContent" :content="renderContent" />
      <div
        v-if="originalSubtitle"
        class="flex flex-col gap-0.5 pt-1.5 -mt-1.5 border-t border-n-alpha-2"
      >
        <span class="text-[10px] uppercase tracking-wide opacity-60">
          {{ $t('CONVERSATION.AUTO_TRANSLATION.ORIGINAL_LABEL') }}
        </span>
        <span class="text-xs opacity-75 whitespace-pre-wrap">
          {{ originalSubtitle }}
        </span>
      </div>
      <TranslationToggle
        v-if="showTranslationToggle"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
